import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Container,
    Paper,
    Grid,
    Divider,
    Chip,
    Avatar,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Event as EventIcon,
    AccessTime as AccessTimeIcon,
    Person as PersonIcon,
    LocationOn as LocationOnIcon,
    Spa as SpaIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../utils/responsiveMedia';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';

const AppointmentConfirmationStep = ({
    salonDetails,
    selectedServices = [],
    selectedDate, // "YYYY-MM-DD"
    selectedSlot, // "HH:mm" string or Chain object { start_time: "..." }
    onConfirm,
    onBack,
    onCancel,
    currentStep = 5,
    totalSteps = 5,
    loading = false,
    employees = []
}) => {
    const { t } = useTranslation();
    const { tokens: theme } = useSalonPublicTheme();

    // --- Formatting Helpers ---
    const formattedDate = useMemo(() => {
        if (!selectedDate) return '';
        return dayjs(selectedDate).locale(t('locale')).format('dddd D MMMM YYYY');
    }, [selectedDate, t]);

    const startTime = useMemo(() => {
        if (!selectedSlot) return '';
        if (typeof selectedSlot === 'string') return selectedSlot;
        if (typeof selectedSlot === 'object') return selectedSlot.start_time;
        return '';
    }, [selectedSlot]);

    // Calculate End Time (Total duration)
    const totalDuration = selectedServices.reduce((acc, s) => acc + (s.duration || 0), 0);

    const endTime = useMemo(() => {
        if (!startTime) return '';
        const [h, m] = startTime.split(':').map(Number);
        return dayjs().hour(h).minute(m).add(totalDuration, 'minute').format('HH:mm');
    }, [startTime, totalDuration]);

    const totalPrice = selectedServices.reduce((acc, s) => acc + parseFloat(s.price || 0), 0);

    // Format info for "Professional"
    // If multiple services have different pros, we might list them or say "Multiple"
    const professionalDisplay = useMemo(() => {
        const distinctPros = new Set();
        selectedServices.forEach(s => {
            if (s.teamMember && s.teamMember !== 'any') {
                // Find name in employees list if available
                const emp = employees.find(e => e.id === Number(s.teamMember) || e.team_member_id === Number(s.teamMember));
                if (emp) {
                    const name = emp.user_details?.full_name || emp.user_details?.first_name || 'Expert';
                    distinctPros.add(name);
                } else {
                    distinctPros.add('Expert #' + s.teamMember);
                }
            }
        });

        if (distinctPros.size === 0) return t('client_services.default_expert');
        if (distinctPros.size === 1) return Array.from(distinctPros)[0];
        return t('client_services.multiple_experts');
    }, [selectedServices, employees]);


    return (
        <Container
            data-testid="appointment-confirmation-theme-root"
            maxWidth="lg"
            style={{ fontFamily: theme.typography.fontFamily.sans.join(', ') }}
            sx={{
            py: { xs: 0, md: 0 },
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            [HIGH_DENSITY_TABLET_MEDIA]: {
                maxWidth: 'min(94vw, 1500px)',
            },
        }}>
            {/* Contenu principal */}
            <Box sx={{ 
                flex: 1,
                maxWidth: 1200, 
                mx: 'auto',
                width: '100%',
                px: { xs: 1, md: 0 },
                pb: { xs: 12, md: 4 },
                pt: 1, // Petit espace entre le bouton et le contenu
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    maxWidth: 1420,
                },
            }}>
                {/* Header Navigation */}
                <Box sx={{ mb: 4, pt: 1, [HIGH_DENSITY_TABLET_MEDIA]: { mb: 5 } }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr auto',
                            alignItems: 'center',
                            gap: 2,
                            mb: 1,
                        }}
                    >
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={onBack}
                            sx={{
                                color: theme.colors.gray[600],
                                textTransform: 'none',
                                fontWeight: 700,
                                px: 1,
                                py: 0.75,
                                minWidth: 'auto',
                                borderRadius: 2,
                                justifySelf: 'start',
                                '&:hover': {
                                    backgroundColor: theme.colors.gray[100],
                                }
                            }}
                        >
                            {t('client_services.back')}
                        </Button>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.colors.gray[500],
                                display: 'block',
                                textAlign: 'center',
                                [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '0.9rem' },
                            }}
                        >
                            {t('client_services.breadcrumb')}
                        </Typography>
                        <Box sx={{ width: { xs: 72, sm: 96 } }} />
                    </Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: theme.colors.gray[900],
                            mb: 1,
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                fontSize: 'clamp(2.8rem, 4.5vw, 4rem)',
                            },
                        }}
                    >
                        {t('client_services.verify_info')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.colors.gray[500], [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.2rem' } }}>
                        {t('client_services.confirm_details')}
                    </Typography>
                </Box>

            {/* Main Card */}
            <Paper elevation={0} sx={{
                p: 0,
                borderRadius: 4,
                border: `1px solid ${theme.colors.gray[200]}`,
                overflow: 'hidden',
                backgroundColor: theme.salon.compatibilityMode ? '#fff' : theme.salon.surface,
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    borderRadius: 5,
                },
            }}>



                {/* Content Split */}
                <Grid container>
                    {/* Left: Services */}
                    <Grid item xs={12} md={6} sx={{ p: 4, borderRight: { md: `1px solid ${theme.colors.gray[100]}` }, borderBottom: { xs: `1px solid ${theme.colors.gray[100]}`, md: 'none' }, [HIGH_DENSITY_TABLET_MEDIA]: { p: 5.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <SpaIcon sx={{ color: theme.colors.primary[500], mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.5rem' } }}>
                                {t('client_services.selected_services')}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {selectedServices.map((service, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.2rem' } }}>
                                            {service.serviceName || service.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: theme.colors.primary[500], display: 'block', mt: 0.5, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '0.95rem' } }}>
                                            {service.formatted_duration || (service.duration + ' min')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.2rem' } }}>
                                        {service.price} DA
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    {/* Right: Details */}
                    <Grid item xs={12} md={6} sx={{ p: 4, bgcolor: theme.salon.surfaceMuted, [HIGH_DENSITY_TABLET_MEDIA]: { p: 5.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <EventIcon sx={{ color: theme.colors.primary[500], mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.5rem' } }}>
                                {t('client_services.appointment_details')}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Professional */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: theme.salon.compatibilityMode ? '#fff' : theme.salon.surface, p: 2, borderRadius: 3, [HIGH_DENSITY_TABLET_MEDIA]: { p: 3 } }}>
                                <Avatar sx={{ bgcolor: theme.colors.primary[100], color: theme.colors.primary[600] }}>
                                    <PersonIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ color: theme.colors.primary[500], fontWeight: 700, fontSize: '0.7rem', [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '0.9rem' } }}>{t('client_services.professional')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.1rem' } }}>{professionalDisplay}</Typography>
                                </Box>
                            </Box>

                            {/* Date & Time */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: theme.salon.compatibilityMode ? '#fff' : theme.salon.surface, p: 2, borderRadius: 3, [HIGH_DENSITY_TABLET_MEDIA]: { p: 3 } }}>
                                <Avatar sx={{ bgcolor: theme.colors.primary[100], color: theme.colors.primary[600] }}>
                                    <EventIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ color: theme.colors.primary[500], fontWeight: 700, fontSize: '0.7rem', [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '0.9rem' } }}>{t('client_services.date_time')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize', [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.1rem' } }}>
                                        {formattedDate} {t('client_services.at')} {startTime}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Duration */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: theme.salon.compatibilityMode ? '#fff' : theme.salon.surface, p: 2, borderRadius: 3, [HIGH_DENSITY_TABLET_MEDIA]: { p: 3 } }}>
                                <Avatar sx={{ bgcolor: theme.colors.primary[100], color: theme.colors.primary[600] }}>
                                    <AccessTimeIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ color: theme.colors.primary[500], fontWeight: 700, fontSize: '0.7rem', [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '0.9rem' } }}>{t('client_services.total_duration')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.1rem' } }}>
                                        {Math.floor(totalDuration / 60) > 0 ? `${Math.floor(totalDuration / 60)}h ` : ''}{totalDuration % 60}min
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Footer Total */}
                <Box sx={{ p: 4, bgcolor: theme.colors.primary[50], borderTop: `1px solid ${theme.colors.gray[100]}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, [HIGH_DENSITY_TABLET_MEDIA]: { p: 5.5 } }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.colors.gray[900], [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1.2rem' } }}>
                            {t('client_services.total_amount')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.colors.primary[500], [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '1rem' } }}>
                            {t('client_services.payment_on_site')}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.primary[600], [HIGH_DENSITY_TABLET_MEDIA]: { fontSize: '3rem' } }}>
                            {totalPrice.toFixed(2)} DA
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Actions Button */}
            <Box sx={{ mt: 4, mb: 4 }}>
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={onConfirm}
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                    sx={{
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        [HIGH_DENSITY_TABLET_MEDIA]: {
                            fontSize: '1.25rem',
                        },
                    }}
                >
                    {loading ? t('client_services.validating') : t('client_services.confirm_appointment')}
                </Button>

                <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={onCancel}
                    disabled={loading}
                    startIcon={<CloseIcon />}
                    sx={{
                        mt: 2,
                        fontWeight: 700,
                        fontSize: '1rem',
                        [HIGH_DENSITY_TABLET_MEDIA]: {
                            fontSize: '1.15rem',
                        },
                    }}
                >
                    {t('common.cancel')}
                </Button>

                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: theme.colors.gray[400] }}>
                    {t('client_services.terms_acceptance')}
                </Typography>

                {/* <Button
                    onClick={onBack}
                    sx={{ display: 'block', mx: 'auto', mt: 2, color: theme.colors.gray[500], textTransform: 'none' }}
                    startIcon={<ArrowBackIcon />}
                >
                    Modifier ma réservation
                </Button> */}
            </Box>
            </Box>
        </Container>
    );
};

export default AppointmentConfirmationStep;
